import { useState } from "react";
import { LockSimple, Package, Plus, Spinner } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import {
  usersQueryKeys,
  useUserBundleAccess,
  useUserCourseAccess,
  useUsers,
} from "@/hooks";
import i18n from "@/i18n";
import { MODAL_ROUTES } from "@/router";
import { useNavigateModal } from "@/router/useNavigateModal";
import type { ModalProps } from "@/shared.types";
import type {
  PlanName,
  User,
  UserBundleAccess,
  UserCourseAccess,
} from "@/types";
import {
  mapBackendStatusToDisplayStatus,
  mapDisplayStatusToColor,
} from "@/types/models/userBundleAccesses";
import {
  Badge,
  Button,
  CheckboxGroup,
  Circle,
  errorToast,
  Heading,
  Modal,
  RadioButtonGroup,
} from "@/ui";
import { mapCourseTypeLabel } from "@/utils/courseUtils";
import { formatDateToDDMMYYYY } from "@/utils/dates";
import {
  mapPlansToAvailablePlanNames,
  transformPlanNamesToApiFormat,
} from "@/utils/planUtils";

interface LocationState {
  user: User;
}

export const ManageAccessLevelModal = ({ show, onClose }: ModalProps) => {
  const location = useLocation();
  const state = location.state as LocationState;
  const navigateModal = useNavigateModal();

  if (!state?.user) {
    errorToast(i18n.t("error.general"));
  }

  const user = state.user;

  const { useUserBundleAccessesForUser } = useUserBundleAccess();
  const { data: bundleAccessData, isLoading: isLoadingBundleAccesses } =
    useUserBundleAccessesForUser(user.id);
  const bundleAccesses: UserBundleAccess[] = bundleAccessData?.data ?? [];

  const { useUserCourseAccessesForUser } = useUserCourseAccess();
  const { data: courseAccessData, isLoading: isLoadingCourseAccesses } =
    useUserCourseAccessesForUser(user.id);
  const courseAccesses: UserCourseAccess[] = courseAccessData?.data ?? [];

  const isLoadingAccesses = isLoadingBundleAccesses || isLoadingCourseAccesses;

  const [showAllAccess, setShowAllAccess] = useState(false);
  const ACCESS_LIMIT = 3;
  const totalAccessCount = bundleAccesses.length + courseAccesses.length;
  const shownBundles = showAllAccess
    ? bundleAccesses
    : bundleAccesses.slice(0, ACCESS_LIMIT);
  const remainingSlots = Math.max(0, ACCESS_LIMIT - shownBundles.length);
  const shownCourses = showAllAccess
    ? courseAccesses
    : courseAccesses.slice(0, remainingSlots);

  const previouslySelectedPlans = mapPlansToAvailablePlanNames(user.plans);

  const { control, watch } = useForm<{
    radioOption: string[];
    checkboxOption: PlanName[];
  }>({
    defaultValues: {
      radioOption: ["self_paced", "courses", "trainings", "practices_yearly"],
      checkboxOption: previouslySelectedPlans,
    },
  });
  const selectedRadioValue = watch("radioOption");
  const selectedCheckboxValue = watch("checkboxOption");

  const radioOptions = [
    {
      label: i18n.t("users.full_access"),
      value: ["self_paced", "courses", "trainings", "practices_yearly"],
    },
    { label: i18n.t("users.choose_specific_subscriptions"), value: [""] },
    { label: i18n.t("users.no_free_access_option"), value: ["__none__"] },
  ];

  const checkboxOptions = [
    {
      label: (
        <span className="font-bold">{i18n.t("users.self-paced_course")}</span>
      ),
      value: "self_paced",
    },
    {
      label: (
        <div>
          <span className="font-bold">{i18n.t("users.course_materials")}</span>
          <p className="text-sm text-neutral-700">
            {i18n.t("users.enabling_this_option_does_not_provide_access")}
          </p>
        </div>
      ),
      value: "courses",
    },
    {
      label: (
        <div>
          <span className="font-bold">{i18n.t("users.teacher_materials")}</span>
          <p className="text-sm text-neutral-700">
            {i18n.t("users.enabling_this_option_does_not_provide_access")}
          </p>
        </div>
      ),
      value: "trainings",
    },
    {
      label: (
        <div>
          <span className="font-bold">
            {i18n.t("users.mindfulness_practices")}
          </span>
          <p className="text-sm text-neutral-700">
            {t("users.users_wont_be_able_to_view_the_course_practices")}
          </p>
        </div>
      ),
      value: "practices_yearly",
    },
  ];

  const queryClient = useQueryClient();
  const { useUpdateUserAccessLevels } = useUsers();
  const { mutate: updateUserAccessLevels, isPending } =
    useUpdateUserAccessLevels();

  const handleSavePlans = () => {
    const userId = user.id;

    let planNames: PlanName[];
    if (selectedRadioValue[0] === "__none__") {
      // Remove all free access.
      planNames = [];
    } else if (selectedRadioValue[0] === "") {
      planNames = selectedCheckboxValue;
    } else {
      planNames = selectedRadioValue as PlanName[];
    }

    const accessLevels = transformPlanNamesToApiFormat(planNames);

    updateUserAccessLevels(
      { userId, accessLevels },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: usersQueryKeys.useUsers(),
            exact: false,
          });
          onClose();
        },
        onError: (err) => {
          errorToast(err);
        },
      },
    );
  };

  return (
    <Modal
      show={show}
      title={
        <div className="flex w-full flex-col gap-y-2 sm:w-[520px]">
          <Heading
            containerClassName="items-center"
            heading={t("users.manage_access_levels")}
            left={
              <Circle>
                <LockSimple size={24} className="text-tertiary-800" />
              </Circle>
            }
          />
          <p className="text-base text-neutral-700">
            {`${t("users.access_for")} ${user.email}`}
          </p>
        </div>
      }
      className="py-10"
      onClose={onClose}
    >
      <div className="flex flex-col gap-y-8">
        {/* Bundle & Course Access */}
        <div className="flex flex-col gap-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-tertiary-600">
              {t("users.bundle_and_course_access")}
            </span>
            <Button
              variant="outlined"
              onClick={() =>
                navigateModal(MODAL_ROUTES.grantBundleAccess, { user })
              }
            >
              <Plus size={14} className="mr-1.5" />
              {t("materials.grant_access")}
            </Button>
          </div>
          <p className="text-xs text-neutral-500">
            {t("users.specific_courses_help")}
          </p>

          {isLoadingAccesses ? (
            <div className="flex items-center justify-center py-6">
              <Spinner className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : bundleAccesses.length === 0 && courseAccesses.length === 0 ? (
            <div className="flex items-center gap-x-2 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
              <Package size={16} className="text-neutral-400" />
              <span className="text-sm text-neutral-500">
                {t("users.no_bundle_access")}
              </span>
            </div>
          ) : (
            <>
              <div className="flex flex-col divide-y divide-neutral-100 rounded-xl border border-neutral-200">
                {shownBundles.map((access) => (
                  <div
                    key={access.id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div className="flex flex-col gap-y-0.5">
                      <div className="flex items-center gap-x-1.5">
                        <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
                          {t("materials.bundle")}
                        </span>
                        <span className="text-sm font-medium text-neutral-900">
                          {access.bundle?.name ?? "—"}
                        </span>
                      </div>
                      <span className="text-xs text-neutral-500">
                        {(() => {
                          const parts: string[] = [];
                          if (access.bundle?.type) {
                            parts.push(mapCourseTypeLabel(access.bundle.type));
                          }
                          if (access.access_start_date) {
                            parts.push(
                              `${t("materials.access_from")} ${formatDateToDDMMYYYY(access.access_start_date)}`,
                            );
                          }
                          parts.push(
                            access.access_end_date
                              ? `${t("materials.expires")} ${formatDateToDDMMYYYY(access.access_end_date)}`
                              : t("materials.indefinite"),
                          );
                          return parts.join(" · ");
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <Badge
                        text={mapBackendStatusToDisplayStatus(access.status)}
                        color={mapDisplayStatusToColor(access.status)}
                      />
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigateModal(MODAL_ROUTES.manageUserBundleAccess, {
                            userBundleAccess: access,
                          })
                        }
                        disabled={access.status === "withdrawn"}
                      >
                        {t("materials.manage_access")}
                      </Button>
                    </div>
                  </div>
                ))}
                {shownCourses.map((access) => (
                  <div
                    key={access.id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div className="flex flex-col gap-y-0.5">
                      <div className="flex items-center gap-x-1.5">
                        <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
                          {t("materials.course")}
                        </span>
                        <span className="text-sm font-medium text-neutral-900">
                          {access.course?.name ?? "—"}
                        </span>
                      </div>
                      <span className="text-xs text-neutral-500">
                        {(() => {
                          const parts: string[] = [];
                          if (access.access_start_date) {
                            parts.push(
                              `${t("materials.access_from")} ${formatDateToDDMMYYYY(access.access_start_date)}`,
                            );
                          }
                          parts.push(
                            access.access_end_date
                              ? `${t("materials.expires")} ${formatDateToDDMMYYYY(access.access_end_date)}`
                              : t("materials.indefinite"),
                          );
                          return parts.join(" · ");
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-x-2">
                      <Badge
                        text={mapBackendStatusToDisplayStatus(access.status)}
                        color={mapDisplayStatusToColor(access.status)}
                      />
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigateModal(MODAL_ROUTES.manageUserCourseAccess, {
                            userCourseAccess: access,
                          })
                        }
                        disabled={access.status === "withdrawn"}
                      >
                        {t("materials.manage_access")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {totalAccessCount > ACCESS_LIMIT && (
                <button
                  type="button"
                  onClick={() => setShowAllAccess((v) => !v)}
                  className="self-start text-sm font-semibold text-primary-700 hover:text-primary-900"
                >
                  {showAllAccess
                    ? "Show less"
                    : `Show all (${totalAccessCount})`}
                </button>
              )}
            </>
          )}
        </div>

        <hr className="border-neutral-100" />

        {/* Plan-based free access */}
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-1">
            <span className="text-sm font-medium text-tertiary-600">
              {t("users.choose_a_type_of_access")}
            </span>
            <p className="text-xs text-neutral-500">
              {t("users.free_subscription_help")}
            </p>
          </div>
          <RadioButtonGroup
            name="radioOption"
            control={control}
            items={radioOptions}
            containerClassName="bg-tertiary-100 gap-x-3 border-none"
            className="px-3 py-2.5"
          />

          {selectedRadioValue[0] === "" && (
            <CheckboxGroup
              name="checkboxOption"
              control={control}
              items={checkboxOptions}
              label={
                <span className="text-sm text-tertiary-600">
                  {t("users.select_one_or_multiple_subscription_types")}
                </span>
              }
              containerClassName="bg-tertiary-100 border-none"
            />
          )}
        </div>

        <div className="flex flex-row justify-center gap-x-2">
          <Button
            onClick={() => onClose()}
            className="flex-grow"
            variant="outlined"
            disabled={isPending}
          >
            {t("general.cancel")}
          </Button>
          <Button
            onClick={() => handleSavePlans()}
            className="flex-grow"
            variant="filled"
            disabled={isPending}
          >
            {isPending ? (
              <Spinner className="h-5 w-5 animate-spin" />
            ) : (
              t("general.save_changes")
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
